import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Upload, Download, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI } from '../../lib/api';
import { compressImageIfNeeded, apiErrorText } from './meetingsUtils';

const KINDS = ['PHOTO', 'VISITING_CARD', 'STOCK_LIST', 'REQUIREMENT_LIST', 'PRESENTATION', 'OTHER'];
const ACCEPT = 'image/*,.pdf,.xlsx,.xls,.csv,.docx,.pptx';

export default function MeetingFilesSection({ meetingId, files, onChanged, canEdit, isVendorMeeting }) {
  const [kind, setKind] = useState('PHOTO');
  const [uploading, setUploading] = useState(false);
  const [sendingToBidEngine, setSendingToBidEngine] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const ready = await compressImageIfNeeded(file);
      await meetingsAPI.uploadFile(meetingId, kind, ready);
      toast.success('File uploaded');
      onChanged();
    } catch (err) { toast.error(apiErrorText(err, 'Failed to upload file')); }
    finally { setUploading(false); }
  };

  const removeFile = async (fileId) => {
    try { await meetingsAPI.deleteFile(meetingId, fileId); toast.success('File removed'); onChanged(); }
    catch (e) { toast.error(apiErrorText(e, 'Failed to remove file')); }
  };

  // Phase 2: a vendor meeting's stock list can be sent straight into Bid Engine.
  const sendToBidEngine = async (fileId) => {
    setSendingToBidEngine(fileId);
    try {
      await meetingsAPI.sendFileToBidEngine(meetingId, fileId);
      toast.success('Sent to Bid Engine — check the Bid Engine jobs list for progress');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to send to Bid Engine')); }
    finally { setSendingToBidEngine(null); }
  };

  return (
    <div className="space-y-2" data-testid="meeting-files-section">
      <p className="text-sm font-semibold">Files & Photos</p>
      {canEdit && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44" data-testid="file-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={inputRef} type="file" accept={ACCEPT} capture="environment" className="hidden" onChange={handleFile} data-testid="file-input" />
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="file-upload-btn">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Upload
          </Button>
        </div>
      )}
      <div className="space-y-1">
        {(files || []).length === 0 && <p className="text-xs text-muted-foreground" data-testid="no-files-msg">No files yet.</p>}
        {(files || []).map((f) => (
          <div key={f.file_id} className="flex items-center gap-2 text-sm border rounded-sm px-2 py-1.5" data-testid={`file-row-${f.file_id}`}>
            <Badge variant="outline" className="text-[10px]">{f.kind.replace(/_/g, ' ')}</Badge>
            <span className="flex-1 truncate">{f.name}</span>
            <a href={meetingsAPI.fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`file-download-${f.file_id}`}>
              <Download className="h-3.5 w-3.5 text-muted-foreground" />
            </a>
            {isVendorMeeting && f.kind === 'STOCK_LIST' && /\.xlsx$/i.test(f.name || '') && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={sendingToBidEngine === f.file_id}
                onClick={() => sendToBidEngine(f.file_id)} data-testid={`file-send-to-bid-engine-${f.file_id}`}>
                {sendingToBidEngine === f.file_id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                Send to Bid Engine
              </Button>
            )}
            {canEdit && (
              <button onClick={() => removeFile(f.file_id)} data-testid={`file-remove-${f.file_id}`}>
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
